import currency from 'currency.js';
import {formatCurrency, parseCurrency} from '../utils/currency-helpers';

export type ValueConverter<TInput, TOutput> = {
  parseInput: (input: TInput) => TOutput;
  format: (value: TOutput) => TInput;
  equals: (value: TOutput, formattedInput: TInput) => boolean;
};

export const defaultValueConverter: ValueConverter<any, any> = {
  format: (x) => x,
  parseInput: (x) => x,
  equals: (value, formattedInput) => value === formattedInput,
};

export const currencyValueConverter: ValueConverter<any, any> = {
  format: (value) => formatCurrency(value / 100),
  parseInput: (input) => parseCurrency(input),
  equals: (value, input) => value === parseCurrency(input),
};

export const decimalValueConverter: ValueConverter<any, any> = {
  format: (value) => currency(value * 100).value.toString(),
  parseInput: (input) => {
    const parsedInput = Number(input);
    if (!Number.isFinite(parsedInput)) {
      return 0;
    }
    const precision = getPrecision(parsedInput) + 2;
    return Number((parsedInput / 100).toPrecision(precision));
  },
  equals: (value, input) => value === decimalValueConverter.parseInput(input),
};

export const booleanToStringConverter: ValueConverter<any, any> = {
  format: (value: boolean): string => value.toString(),
  parseInput: (input: string): boolean => input.toLowerCase() === 'true',
  equals: (value: boolean, formattedInput: string): boolean =>
    value === (formattedInput.toLowerCase() === 'true'),
};

export const selectNumberConverter: ValueConverter<string | null, number> = {
  format: (output) => output.toString(),
  parseInput: (input) => Number(input),
  equals: (output, input) => output === Number(input),
};

export const multiSelectNumberConverter: ValueConverter<string[], number[]> = {
  format: (output) => output.map((num) => num.toString()),
  parseInput: (input) => input.map((num) => Number(num)),
  equals: (output: number[], input: string[]) => {
    if (output.length !== input.length) return false;
    return output.every((value, index) => value === Number(input[index]));
  },
};

export const upperCaseValueConverter: ValueConverter<string, string> = {
  format: (value) => value.toUpperCase(),
  parseInput: (input) => input.toUpperCase(),
  equals: (value, input) => value === input,
};

function pluralValueFormatter(
  value: string,
  singularUnit: string,
  pluralUnit: string
) {
  if (Number.isNaN(value)) {
    return `0 ${pluralUnit}`;
  }

  const numValue = Number(value);

  if (numValue === 1 || numValue === -1) {
    return `${value} ${singularUnit}`;
  }

  return `${value} ${pluralUnit}`;
}

export function minuteFormatter(value: string) {
  return pluralValueFormatter(value, 'minute', 'minutes');
}

export function dayFormatter(value: string) {
  return pluralValueFormatter(value, 'day', 'days');
}

export function hourFormatter(value: string) {
  return pluralValueFormatter(value, 'hour', 'hours');
}

export function parseDigitsFromString(value: string) {
  return value.replace(/\D/g, '');
}

function getPrecision(a) {
  if (!isFinite(a)) return 0;
  let e = 1,
    p = 0;
  while (Math.round(a * e) / e !== a) {
    e *= 10;
    p++;
  }
  return p;
}
